import { inject, Injectable } from '@angular/core';
import { TrackingService } from '@ds24/tracking-ng';
import { ApmProvider, EventPayloadMap, OrderFormPayloadBaseType, TrackingEventType } from '@ds24/tracking-core';
import { combineLatest, firstValueFrom, map, Subscription } from 'rxjs';
import { OrderformFascade } from '../fascades/orderform.fascade';
import { performanceForMetric } from '../misc/performance';
import { getBrowserInfo, getDeviceType } from '../misc/browser-info';

@Injectable({
  providedIn: 'root',
})
export class OrderformTrackingCollectorService {
  #tracking = inject(TrackingService);
  #orderFormFascade = inject(OrderformFascade);

  #orderMetaDataSubscription: Subscription;

  #orderFormPayload: OrderFormPayloadBaseType;

  public async initialize() {
    this.#tracking.registerProvider(
      new ApmProvider({
        disableInstrumentations: ['page-load', 'eventtarget', 'click', 'error', 'history', 'fetch', 'xmlhttprequest'],
      })
    );
    const sessionId = await this.#orderFormFascade.anonymousSessionId;
    this.#tracking.addMetadata({
      sessionId,
    });

    this.#orderFormPayload = {
      iframeEmbedded: window.parent !== window,
      mainProductId: await firstValueFrom(this.#orderFormFascade.mainProductId$),
      mainProductTypeId: await firstValueFrom(this.#orderFormFascade.mainProductTypeId$),
      defaultPrice: await firstValueFrom(this.#orderFormFascade.mainProductDefaultPrice$),
      siteownerId: await firstValueFrom(this.#orderFormFascade.siteownerId$),
      pageType: window.location.pathname.startsWith('/product') ? 'orderform' : 'renew',
      orderType: window.location.search.includes('upsell_session_id') ? 'upsell' : 'initial',
      vendor: await firstValueFrom(this.#orderFormFascade.merchantName$),
      affiliate: await firstValueFrom(this.#orderFormFascade.affiliateName$),
      orderformId: await firstValueFrom(this.#orderFormFascade.config$.pipe(map((config) => config.orderform_id))),
      hasCookieConsent: await firstValueFrom(this.#orderFormFascade.hasCookieConsent$),
    };

    const userProperties = {};
    const experiments = await this.#orderFormFascade.viewedExperiments;
    experiments.forEach((experiment) => {
      userProperties[`experiment_${experiment.key}_enabled`] = true;
      userProperties[`experiment_${experiment.key}_variation`] = experiment.variation;
    });
    this.#tracking.addMetadata({
      userProperties,
    });

    const isGdprApplied = await firstValueFrom(this.#orderFormFascade.isGdprApplied$);
    if (!isGdprApplied) {
      this.#orderFormPayload.referrer = document.referrer || undefined;
      const userAgent = navigator.userAgent;
      const { browser, version, app, os } = getBrowserInfo(userAgent);

      this.#orderFormPayload.os = os;
      this.#orderFormPayload.browser = browser;
      this.#orderFormPayload.browserVersion = version;
      this.#orderFormPayload.app = app;
      this.#orderFormPayload.deviceType = getDeviceType(userAgent);
      this.#orderFormPayload.screenWidth = window.screen.width;
      this.#orderFormPayload.screenHeight = window.screen.height;
    }

    this.#orderMetaDataSubscription = combineLatest([
      this.#orderFormFascade.order$,
      this.#orderFormFascade.isExpressCheckoutClicked$,
    ]).subscribe(([order, isExpressCheckoutClicked]) => {
      this.#orderFormPayload = {
        ...this.#orderFormPayload,
        orderCheckoutType: isExpressCheckoutClicked ? 'express' : 'default',
        orderBuyerCountry: order.settings.config.buyer_country,
        orderCurrency: order.order.currency,
        orderValue: order.order_summary.order_final.gross_1st,
        paymentMethod: isExpressCheckoutClicked
          ? order.express_checkout?.pay_method
          : order.order.payment_provider.renderer_type,
        paymentProviderId: String(
          isExpressCheckoutClicked
            ? order.express_checkout?.payment_provider_id
            : order.order.payment_provider.payment_provider_id
        ),
        orderBillingType: order.order_summary.payment_plan.billing_type,
      };
    });

    this.#tracking.track('OrderFormLoaded', this.#orderFormPayload);

    this.trackOrderFormStart();
  }

  private async trackOrderFormStart() {
    let firstInput = await performanceForMetric('first-input');
    const isGdprApplied = await firstValueFrom(this.#orderFormFascade.isGdprApplied$);
    const firstContentfulPaint = isGdprApplied ? undefined : await performanceForMetric('paint');
    const largestContentfulPaint = isGdprApplied ? undefined : await performanceForMetric('largest-contentful-paint');
    if (firstInput === undefined) {
      firstInput = await performanceForMetric('first-input');
    }
    this.#tracking.track('OrderFormStart', {
      ...this.#orderFormPayload,
      largestContentfulPaint,
      firstContentfulPaint,
      firstInput,
    });
  }

  public track<T extends TrackingEventType>(event: T, payload?: Extract<EventPayloadMap[T], OrderFormPayloadBaseType>) {
    this.#tracking.track(event, {
      ...this.#orderFormPayload,
      ...payload,
    });
  }

  public destroy() {
    this.#tracking.unregisterProvider('apm');
    this.#orderMetaDataSubscription?.unsubscribe();
  }
}
